import * as React from 'react';
import { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  Paper,
  useTheme,
  alpha,
  Skeleton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { verifyInviteToken } from '../../store/userThunk';
import { useSearchParams } from 'react-router-dom';
import { 
  fetchARDashboardNumbers, 
  fetchARDashboardSales, 
  fetchARDashboardInvoices,
  fetchARDashboardAging,
  fetchARDashboardDSOTrend,
  fetchARDashboardCustomersDue,
  fetchARDashboardCustomersDelay
} from '../../store/ARDashboardStore/ARDashboardThunk';
import {
  fetchAPDashboardNumbers,
  fetchAPDashboardSales,
  fetchAPDashboardInvoices,
  fetchAPDashboardAging,
  fetchAPDashboardDSOTrend,
  fetchAPDashboardCustomersDue,
  fetchAPDashboardCustomersDelay
} from '../../store/APDashboardStore/APDashboardThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Chart from 'react-apexcharts';

const Home = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { user, selectedAccount } = useAppSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState('30days');
  const [isLoading, setIsLoading] = useState(false);
  
  const ARDashboard = useAppSelector(state => state.ARDashboard);
  const APDashboard = useAppSelector(state => state.APDashboard);

  useEffect(() => {
    const token = searchParams.get('inviteToken');
    if (token) {
      dispatch(verifyInviteToken({ token }));
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (selectedAccount?._id) {
      fetchDashboardData();
    }
  }, [selectedAccount, period]);

  const fetchDashboardData = async () => {
    if (!selectedAccount?._id) {
      console.warn('No selected account found');
      return;
    }
    
    setIsLoading(true);
    const filters = `period=${period}`;
    
    try {
      // Fetch AR Dashboard Data
      const arPromises = [
        dispatch(fetchARDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardInvoices({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardAging({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardDSOTrend({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardCustomersDue({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardCustomersDelay({ accountId: selectedAccount._id, params: filters }))
      ];

      // Fetch AP Dashboard Data
      const apPromises = [
        dispatch(fetchAPDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardInvoices({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardAging({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardDSOTrend({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardCustomersDue({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardCustomersDelay({ accountId: selectedAccount._id, params: filters }))
      ];

      // Execute all promises with error handling for individual failures
      await Promise.allSettled([...arPromises, ...apPromises]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const MetricCard = ({ 
    title, 
    value, 
    trend,
    icon, 
    bgColor
  }: {
    title: string;
    value: string | number;
    trend?: { hasGrown: boolean; value: number };
    icon: React.ReactNode;
    bgColor: string;
  }) => (
    <Card 
      sx={{ 
        height: 'auto',
        minHeight: '140px',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px 0 rgb(0 0 0 / 0.1)',
          borderColor: bgColor
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {title}
              </Typography>
              <Box
                sx={{
                  backgroundColor: alpha(bgColor, 0.1),
                  borderRadius: 1.5,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {React.cloneElement(icon as React.ReactElement, {
                  sx: { color: bgColor, fontSize: 24 }
                })}
              </Box>
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.1,
                mb: 1
              }}
            >
              {isLoading ? <Skeleton width={120} /> : value}
            </Typography>
            {trend && trend.value !== null && trend.value !== undefined && (
              <Box display="flex" alignItems="center">
                {trend.hasGrown ? (
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
                )}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend.hasGrown ? '#4caf50' : '#f44336', 
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                >
                  {Number(trend.value).toFixed(1)}% vs last period
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Sales Chart Data
  const salesChartOptions = {
    chart: {
      type: 'area' as const,
      height: 350,
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout' as const,
        speed: 800,
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth' as const,
      width: 2
    },
    xaxis: {
      categories: ARDashboard.sales?.dashboardSales?.series ? 
        Object.keys(ARDashboard.sales.dashboardSales.series).map(dateStr => {
          const date = new Date(dateStr);
          return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        }) : []
    },
    yaxis: {
      labels: {
        formatter: (value: number) => Utils.formatNumber(value, true, false)
      }
    },
    colors: ['#1976d2', '#dc004e'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    tooltip: {
      y: {
        formatter: (value: number) => Utils.formatNumber(value, true, false)
      }
    },
    grid: {
      borderColor: '#f0f0f0',
      strokeDashArray: 4,
    }
  };

  const salesChartSeries = [
    {
      name: 'Sales Revenue',
      data: ARDashboard.sales?.dashboardSales?.series ? 
        Object.values(ARDashboard.sales.dashboardSales.series).map(val => Number(val) || 0) : []
    },
    {
      name: 'Purchase Amount',
      data: APDashboard.sales?.dashboardSales?.series ? 
        Object.values(APDashboard.sales.dashboardSales.series).map(val => Number(val) || 0) : []
    }
  ];

  // Aging Chart Data
  const agingChartOptions = {
    chart: {
      type: 'donut' as const,
      height: 350
    },
    labels: ARDashboard.aging?.dashboardAging?.series ? 
      Object.keys(ARDashboard.aging.dashboardAging.series) : [],
    colors: ['#4caf50', '#ff9800', '#f44336', '#9c27b0', '#2196f3'],
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`
    },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => Utils.formatNumber(
                ARDashboard.aging?.dashboardAging?.agingValue || 0, 
                true, 
                false
              )
            }
          }
        }
      }
    }
  };

  const agingChartSeries = ARDashboard.aging?.dashboardAging?.series ? 
    Object.values(ARDashboard.aging.dashboardAging.series).map(val => Number(val) || 0) : [];

  return (
    <Box sx={{ p: 4, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Welcome Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" fontWeight="bold" color="white" gutterBottom>
            Welcome back, {user.name}! 👋
          </Typography>
          <Typography variant="h6" color="white" sx={{ opacity: 0.9, mb: 3 }}>
            Here's what's happening with your business today
          </Typography>
          {/* <Grid container spacing={3}>
            <Grid item>
              <Box>
                <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                  Active Account
                </Typography>
                <Typography variant="h5" color="white" fontWeight="600">
                  {selectedAccount?.name || 'No account selected'}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box>
                <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
                  Current Date
                </Typography>
                <Typography variant="h5" color="white" fontWeight="600">
                  {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            </Grid>
          </Grid> */}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
      </Paper>

      {/* Sales Metrics Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="700">
          Sales Overview
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              label="Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
              <MenuItem value="365days">Last Year</MenuItem>
            </Select>
          </FormControl>
          <IconButton 
            onClick={fetchDashboardData} 
            disabled={isLoading}
            sx={{ 
              backgroundColor: 'white',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'white',
                boxShadow: 2
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Revenue (AR)"
            value={Utils.formatNumber(ARDashboard.numbers?.dashboardNumbers?.totalAR || 0, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<AccountBalanceIcon />}
            bgColor="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Customers"
            value={ARDashboard.numbers?.dashboardNumbers?.totalCustomers || 0}
            icon={<PeopleIcon />}
            bgColor="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Collection Period"
            value={`${ARDashboard.numbers?.dashboardNumbers?.totalARDSO || 0} days`}
            icon={<AccessTimeIcon />}
            bgColor="#00acc1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Sales Value"
            value={Utils.formatNumber(ARDashboard.sales?.dashboardSales?.salesValue || 0, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<TrendingUpIcon />}
            bgColor="#4caf50"
          />
        </Grid>
      </Grid>

      {/* Purchase Metrics Section */}
      <Typography variant="h5" fontWeight="700" gutterBottom sx={{ mb: 3 }}>
        Purchase Overview
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Payables (AP)"
            value={Utils.formatNumber(APDashboard.numbers?.dashboardNumbers?.totalAP || 0, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<ShoppingCartIcon />}
            bgColor="#ff5722"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Vendors"
            value={APDashboard.numbers?.dashboardNumbers?.totalVendors || 0}
            icon={<LocalShippingIcon />}
            bgColor="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Payment Period"
            value={`${APDashboard.numbers?.dashboardNumbers?.totalAPDSO || 0} days`}
            icon={<AccessTimeIcon />}
            bgColor="#795548"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Purchase Value"
            value={Utils.formatNumber(APDashboard.sales?.dashboardSales?.salesValue || 0, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<PaymentIcon />}
            bgColor="#607d8b"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Typography variant="h5" fontWeight="700" gutterBottom sx={{ mb: 3 }}>
        Analytics & Insights
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Sales vs Purchase Trends
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {isLoading || ARDashboard.sales?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={350} />
            ) : (
              <Chart
                options={salesChartOptions}
                series={salesChartSeries}
                type="area"
                height={350}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Aging Analysis
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {isLoading || ARDashboard.aging?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={350} />
            ) : (
              <Chart
                options={agingChartOptions}
                series={agingChartSeries}
                type="donut"
                height={350}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Top Customers/Suppliers Section */}
      <Typography variant="h5" fontWeight="700" gutterBottom sx={{ mb: 3 }}>
        Top Performers
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Top 5 Customers by Outstanding
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 2 }}>
              {isLoading || ARDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(5)].map((_, i) => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)
              ) : (
                ARDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {item.customer?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {Utils.formatNumber(item.totalAmount || 0, true, false)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body1" color="primary" fontWeight="600">
                        {Utils.formatNumber(item.totalOutstanding || 0, true, false)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Outstanding
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Top 5 Vendors by Outstanding
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 2 }}>
              {isLoading || APDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(5)].map((_, i) => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)
              ) : (
                APDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 5).map((item: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {item.customer?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {Utils.formatNumber(item.totalAmount || 0, true, false)}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body1" color="error" fontWeight="600">
                        {Utils.formatNumber(item.totalOutstanding || 0, true, false)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Outstanding
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;